import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Globe, ExternalLink, CheckCircle, XCircle,
  AlertTriangle, Save, Smartphone, Monitor, Shield, Building2, MapPin, Phone, Mail, Calendar
} from 'lucide-react';
import { prospectsApi } from '../api/prospects';
import type { Prospect, ProspectStatus, PageSpeedMetrics, CompanyRevenue } from '../types';
import { useLang } from '../context/LangContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const scoreColor = (s: number) => s >= 90 ? 'text-green-400' : s >= 50 ? 'text-yellow-400' : 'text-red-400';
const scoreBg    = (s: number) => s >= 90 ? 'bg-green-400' : s >= 50 ? 'bg-yellow-400' : 'bg-red-400';
const fmtMs = (ms: number) => ms >= 1000 ? `${(ms/1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
const fmtRon = (v: number) => v >= 1000000 ? `${(v/1000000).toFixed(2)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v);

// ─── Score Ring ───────────────────────────────────────────────────────────────
const ScoreRing = ({ score, size = 90, label }: { score: number; size?: number; label?: string }) => {
  const c = score >= 90 ? '#4ade80' : score >= 50 ? '#facc15' : '#f87171';
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={7} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={7}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dasharray 1s ease' }} />
        <text x={size/2} y={size/2 + 5} textAnchor="middle" fill={c} fontSize={size > 70 ? 20 : 14} fontWeight="800">{score}</text>
        <text x={size/2} y={size/2 + 18} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize={9}>/100</text>
      </svg>
      {label && <span className="text-xs text-white/40">{label}</span>}
    </div>
  );
};

// ─── Metric Bar ───────────────────────────────────────────────────────────────
const MetricBar = ({ label, value, format, thresholds }: {
  label: string; value: number; format: (v: number) => string;
  thresholds: [number, number]; // [good, needs improvement]
}) => {
  const isGood = value <= thresholds[0];
  const isOk = value <= thresholds[1];
  const color = isGood ? 'bg-green-400' : isOk ? 'bg-yellow-400' : 'bg-red-400';
  const textColor = isGood ? 'text-green-400' : isOk ? 'text-yellow-400' : 'text-red-400';
  const pct = Math.min(100, (value / (thresholds[1] * 2)) * 100);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-white/50">{label}</span>
        <span className={`text-xs font-bold ${textColor}`}>{format(value)}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ─── Revenue Bar Chart ────────────────────────────────────────────────────────
const RevenueChart = ({ data }: { data: CompanyRevenue[] }) => {
  const maxVal = Math.max(...data.map(d => d.cifraAfaceri), 1);
  return (
    <div className="space-y-3">
      {data.map(d => (
        <div key={d.year} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/50 font-semibold">{d.year}</span>
            <div className="flex items-center gap-3 text-white/40">
              <span>CA: <span className="text-white font-bold">{fmtRon(d.cifraAfaceri)} RON</span></span>
              {d.profitNet > 0 && <span>Profit: <span className="text-green-400 font-bold">{fmtRon(d.profitNet)} RON</span></span>}
              {d.angajati > 0 && <span>👤 {d.angajati}</span>}
            </div>
          </div>
          <div className="h-7 bg-white/5 rounded-lg overflow-hidden relative">
            <div className="absolute inset-y-0 left-0 bg-[#295CA8]/60 rounded-lg transition-all duration-700"
              style={{ width: `${(d.cifraAfaceri / maxVal) * 100}%` }} />
            {d.profitNet > 0 && (
              <div className="absolute inset-y-0 left-0 bg-green-500/40 rounded-lg"
                style={{ width: `${(d.profitNet / maxVal) * 100}%` }} />
            )}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-4 pt-1 text-xs text-white/30">
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded bg-[#295CA8]/60 inline-block" />Cifra de afaceri</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded bg-green-500/40 inline-block" />Profit net</span>
      </div>
    </div>
  );
};

// ─── PageSpeed Panel ──────────────────────────────────────────────────────────
const PageSpeedPanel = ({ m, label }: { m: PageSpeedMetrics; label: string }) => (
  <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-5">
    <div className="flex items-center gap-4">
      {label === 'Mobile' ? <Smartphone size={18} className="text-[#4ab8ff]" /> : <Monitor size={18} className="text-[#4ab8ff]" />}
      <div className="flex-1">
        <div className="text-sm font-semibold text-white">{label}</div>
        <div className="text-xs text-white/30">PageSpeed Insights</div>
      </div>
      <ScoreRing score={m.score} size={72} />
    </div>
    <div className="space-y-3">
      <MetricBar label="First Contentful Paint" value={m.fcp} format={fmtMs} thresholds={[1800, 3000]} />
      <MetricBar label="Largest Contentful Paint" value={m.lcp} format={fmtMs} thresholds={[2500, 4000]} />
      <MetricBar label="Total Blocking Time" value={m.tbt} format={fmtMs} thresholds={[200, 600]} />
      <MetricBar label="Speed Index" value={m.si} format={fmtMs} thresholds={[3400, 5800]} />
      <MetricBar label="Time to First Byte" value={m.ttfb} format={fmtMs} thresholds={[800, 1800]} />
      <div className="flex items-center justify-between pt-1 border-t border-white/5">
        <span className="text-xs text-white/40">Cumulative Layout Shift</span>
        <span className={`text-xs font-bold ${m.cls <= 0.1 ? 'text-green-400' : m.cls <= 0.25 ? 'text-yellow-400' : 'text-red-400'}`}>
          {m.cls.toFixed(3)} {m.cls <= 0.1 ? '✓' : m.cls <= 0.25 ? '⚠' : '✗'}
        </span>
      </div>
    </div>
  </div>
);

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const STATUS_CFG: Record<ProspectStatus, { ro: string; color: string }> = {
  new:       { ro: 'Nou',        color: 'bg-blue-500/20 text-blue-400' },
  contacted: { ro: 'Contactat',  color: 'bg-yellow-500/20 text-yellow-400' },
  qualified: { ro: 'Calificat',  color: 'bg-purple-500/20 text-purple-400' },
  proposal:  { ro: 'Propunere',  color: 'bg-orange-500/20 text-orange-400' },
  lost:      { ro: 'Pierdut',    color: 'bg-red-500/20 text-red-400' },
  converted: { ro: 'Convertit',  color: 'bg-green-500/20 text-green-400' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ProspectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLang();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [tab, setTab] = useState<'sumar'|'pagespeed'|'seo'|'tehnic'|'whois'|'companie'|'note'>('sumar');
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ contactName: '', contactEmail: '', contactPhone: '', notes: '', status: 'new' as ProspectStatus, priority: 'medium' as 'low'|'medium'|'high' });

  const load = useCallback(async () => {
    if (!id) return;
    const p = await prospectsApi.get(id);
    setProspect(p);
    setForm({ contactName: p.contactName||'', contactEmail: p.contactEmail||'', contactPhone: p.contactPhone||'', notes: p.notes||'', status: p.status, priority: p.priority });
    if (p.analysisStatus === 'pending') runAnalysis(p);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runAnalysis = async (p: Prospect) => {
    setAnalyzing(true);
    setProspect(prev => prev ? { ...prev, analysisStatus: 'analyzing' } : prev);
    const res = await prospectsApi.analyze(p.id, p.url);
    const updated = await prospectsApi.get(p.id);
    setProspect(updated);
    setAnalyzing(false);
    if (res.ok) setTab('sumar');
  };

  const save = async () => {
    if (!prospect) return;
    setSaving(true);
    await prospectsApi.update(prospect.id, { ...form });
    setSaving(false);
  };

  if (!prospect) return <div className="flex items-center justify-center h-64 text-white/30"><RefreshCw size={24} className="animate-spin mr-2" />Se încarcă...</div>;

  const a = prospect.analysis;
  const isRo = prospect.domain.endsWith('.ro');

  const tabs = [
    { id: 'sumar', label: 'Sumar' },
    { id: 'pagespeed', label: 'PageSpeed', disabled: !a?.pagespeed },
    { id: 'seo', label: 'SEO', disabled: !a?.seo },
    { id: 'tehnic', label: 'Tehnic', disabled: !a },
    { id: 'whois', label: 'WHOIS', disabled: !a?.whois || !isRo },
    { id: 'companie', label: 'Companie', disabled: !a?.company },
    { id: 'note', label: 'Note & Contact' },
  ].filter(tab => !tab.disabled || tab.id === 'sumar' || tab.id === 'note') as { id: typeof tab; label: string }[];

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/prospects')} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all mt-1">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-white truncate">{prospect.name || prospect.domain}</h1>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CFG[prospect.status].color}`}>{STATUS_CFG[prospect.status].ro}</span>
            {prospect.priority === 'high' && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">🔥 Prioritar</span>}
          </div>
          <a href={prospect.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-[#4ab8ff] hover:underline mt-0.5 w-fit">
            <Globe size={13} /> {prospect.domain} <ExternalLink size={11} />
          </a>
          {a && <div className="text-xs text-white/25 mt-0.5">Analizat: {a.analyzedAt} · {(a.durationMs/1000).toFixed(1)}s</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => runAnalysis(prospect)} disabled={analyzing}
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-3 py-2 rounded-lg text-sm transition-all disabled:opacity-50">
            <RefreshCw size={14} className={analyzing ? 'animate-spin' : ''} />
            {analyzing ? 'Se analizează...' : 'Re-analizează'}
          </button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50">
            <Save size={14} /> {saving ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>

      {/* Analyzing banner */}
      {analyzing && (
        <div className="flex items-center gap-3 bg-[#295CA8]/20 border border-[#295CA8]/40 rounded-xl px-5 py-4 text-sm text-[#4ab8ff]">
          <RefreshCw size={16} className="animate-spin flex-shrink-0" />
          <div>
            <div className="font-semibold">Analiză în curs...</div>
            <div className="text-xs text-[#4ab8ff]/60 mt-0.5">PageSpeed · Detectare platformă · SEO · WHOIS {isRo ? '· Date companie' : ''}</div>
          </div>
        </div>
      )}

      {/* Quick score overview — always visible */}
      {a?.pagespeed && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-4 flex flex-col items-center gap-1">
            <Smartphone size={14} className="text-white/30" />
            <ScoreRing score={a.pagespeed.mobile.score} size={60} />
            <span className="text-xs text-white/40">Mobile</span>
          </div>
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-4 flex flex-col items-center gap-1">
            <Monitor size={14} className="text-white/30" />
            <ScoreRing score={a.pagespeed.desktop.score} size={60} />
            <span className="text-xs text-white/40">Desktop</span>
          </div>
          {a.seo && (
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-4 flex flex-col items-center gap-1">
              <span className="text-xs text-white/30">SEO</span>
              <ScoreRing score={a.seo.score} size={60} />
              <span className="text-xs text-white/40">Score</span>
            </div>
          )}
          {a.platform && (
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center gap-1">
              <span className="text-2xl">
                {a.platform === 'WordPress' ? '🔷' : a.platform === 'Wix' ? '🔶' : a.platform === 'Shopify' ? '🟢' : a.platform === 'Next.js' ? '⬛' : '🔵'}
              </span>
              <span className="text-xs font-bold text-white text-center">{a.platform}</span>
              {a.platformVersion && <span className="text-xs text-white/30">v{a.platformVersion}</span>}
            </div>
          )}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center gap-2">
            {a.ssl?.valid
              ? <><Shield size={22} className="text-green-400" /><span className="text-xs text-green-400 text-center">SSL Valid</span><span className="text-xs text-white/25">{a.ssl.issuer}</span></>
              : <><Shield size={22} className="text-red-400" /><span className="text-xs text-red-400">Fără SSL!</span></>
            }
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-white/10 overflow-x-auto">
        {tabs.map(({ id: tid, label }) => (
          <button key={tid} onClick={() => setTab(tid as typeof tab)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${tab === tid ? 'text-[#4ab8ff] border-[#295CA8]' : 'text-white/40 border-transparent hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── SUMAR TAB ── */}
      {tab === 'sumar' && (
        <div className="space-y-5">
          {!a && !analyzing && (
            <div className="py-16 text-center border border-dashed border-white/10 rounded-2xl">
              <RefreshCw size={32} className="mx-auto mb-3 text-white/20" />
              <p className="text-white/30 text-sm">Nicio analiză disponibilă.</p>
              <button onClick={() => runAnalysis(prospect)} className="mt-4 text-[#4ab8ff] text-sm hover:underline">Rulează analiza acum →</button>
            </div>
          )}
          {a && (
            <>
              {/* Issues & Passes */}
              {a.seo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#07090f] border border-red-500/20 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2"><XCircle size={15} />{a.seo.issues.length} Probleme detectate</h3>
                    <ul className="space-y-2">
                      {a.seo.issues.map((issue, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-white/60">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">✗</span>{issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#07090f] border border-green-500/20 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-2"><CheckCircle size={15} />{a.seo.passes.length} Verificări OK</h3>
                    <ul className="space-y-2">
                      {a.seo.passes.map((pass, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-white/60">
                          <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>{pass}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Tech stack */}
              {a.techStack && a.techStack.length > 0 && (
                <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Stack tehnologic detectat</h3>
                  <div className="flex flex-wrap gap-2">
                    {a.techStack.map(t => (
                      <span key={t} className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/70">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors/warnings */}
              {(a.errors.length > 0 || a.warnings.length > 0) && (
                <div className="space-y-2">
                  {a.errors.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300">
                      <XCircle size={13} className="flex-shrink-0" />{e}
                    </div>
                  ))}
                  {a.warnings.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-xs text-yellow-300">
                      <AlertTriangle size={13} className="flex-shrink-0" />{w}
                    </div>
                  ))}
                </div>
              )}

              {/* Company preview */}
              {a.company && (
                <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Building2 size={15} className="text-[#4ab8ff]" />{a.company.name}</h3>
                    <button onClick={() => setTab('companie')} className="text-xs text-[#4ab8ff] hover:underline">Vezi detalii →</button>
                  </div>
                  {a.company.revenue.length > 0 && <RevenueChart data={a.company.revenue} />}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PAGESPEED TAB ── */}
      {tab === 'pagespeed' && a?.pagespeed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <PageSpeedPanel m={a.pagespeed.mobile} label="Mobile" />
          <PageSpeedPanel m={a.pagespeed.desktop} label="Desktop" />

          <div className="md:col-span-2 bg-[#07090f] border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Core Web Vitals — legendă</h3>
            <div className="grid grid-cols-3 gap-4 text-xs">
              {[
                { label: 'Bun', color: 'bg-green-400', desc: 'FCP ≤ 1.8s · LCP ≤ 2.5s · CLS ≤ 0.1' },
                { label: 'Necesită îmbunătățire', color: 'bg-yellow-400', desc: 'FCP ≤ 3s · LCP ≤ 4s · CLS ≤ 0.25' },
                { label: 'Slab', color: 'bg-red-400', desc: 'FCP > 3s · LCP > 4s · CLS > 0.25' },
              ].map(({ label, color, desc }) => (
                <div key={label} className="flex items-start gap-2">
                  <span className={`w-3 h-3 rounded-full ${color} flex-shrink-0 mt-0.5`} />
                  <div><div className="font-semibold text-white/70">{label}</div><div className="text-white/30 mt-0.5">{desc}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SEO TAB ── */}
      {tab === 'seo' && a?.seo && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 md:col-span-2 space-y-4">
              <h3 className="text-sm font-semibold text-white">Meta tags</h3>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-white/40 uppercase tracking-wide">Title</span>
                  <span className={`text-xs font-bold ${a.seo.titleLength >= 50 && a.seo.titleLength <= 60 ? 'text-green-400' : 'text-yellow-400'}`}>{a.seo.titleLength} car.</span>
                </div>
                <div className="bg-white/5 rounded-lg px-3 py-2 text-sm text-white">{a.seo.title || '—'}</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-white/40 uppercase tracking-wide">Meta description</span>
                  <span className={`text-xs font-bold ${a.seo.descriptionLength >= 120 && a.seo.descriptionLength <= 160 ? 'text-green-400' : 'text-yellow-400'}`}>{a.seo.descriptionLength} car.</span>
                </div>
                <div className="bg-white/5 rounded-lg px-3 py-2 text-sm text-white/70">{a.seo.description || '—'}</div>
              </div>
              {a.seo.h1s.length > 0 && (
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wide mb-1">H1 ({a.seo.h1s.length})</div>
                  {a.seo.h1s.map((h, i) => <div key={i} className="bg-white/5 rounded px-3 py-1.5 text-sm text-white/70 mb-1">{h}</div>)}
                </div>
              )}
            </div>
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-white mb-1">Checklist SEO</h3>
              {[
                [a.seo.isIndexable, 'Pagina este indexabilă'],
                [a.seo.hasRobots, 'Robots.txt prezent'],
                [a.seo.hasSitemap, 'Sitemap.xml prezent'],
                [!!a.seo.canonical, 'Canonical tag setat'],
                [a.seo.hasOgTags, 'Open Graph tags prezente'],
                [a.seo.titleLength >= 50 && a.seo.titleLength <= 60, 'Titlu lungime optimă (50-60)'],
                [a.seo.descriptionLength >= 120 && a.seo.descriptionLength <= 160, 'Description optimă (120-160)'],
                [a.seo.h1s.length === 1, 'Un singur H1 pe pagină'],
                [a.ssl?.valid ?? false, 'HTTPS activ'],
              ].map(([ok, label], i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm">
                  {ok ? <CheckCircle size={14} className="text-green-400 flex-shrink-0" /> : <XCircle size={14} className="text-red-400 flex-shrink-0" />}
                  <span className={ok ? 'text-white/70' : 'text-white/40'}>{label as string}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TEHNIC TAB ── */}
      {tab === 'tehnic' && a && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white">Platformă detectată</h3>
              {a.platform ? (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{a.platform === 'WordPress' ? '🔷' : a.platform === 'Wix' ? '🔶' : a.platform === 'Shopify' ? '🟢' : a.platform === 'Next.js' ? '⬛' : '🔵'}</span>
                    <div>
                      <div className="font-bold text-white">{a.platform}</div>
                      {a.platformVersion && <div className="text-xs text-white/40">Versiune: {a.platformVersion}</div>}
                    </div>
                  </div>
                  {a.techStack && a.techStack.length > 0 && (
                    <div>
                      <div className="text-xs text-white/40 mb-2 uppercase tracking-wide">Tech stack complet</div>
                      <div className="flex flex-wrap gap-2">
                        {a.techStack.map(t => <span key={t} className="px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/60">{t}</span>)}
                      </div>
                    </div>
                  )}
                </>
              ) : <p className="text-sm text-white/30">Platforma nu a putut fi detectată</p>}
            </div>

            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white">Securitate & Tehnic</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-sm text-white/50">HTTP Status</span>
                  <span className={`text-sm font-bold ${a.httpStatus === 200 ? 'text-green-400' : 'text-red-400'}`}>{a.httpStatus ?? '—'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-sm text-white/50">SSL/HTTPS</span>
                  {a.ssl?.valid
                    ? <span className="text-sm font-bold text-green-400 flex items-center gap-1"><Shield size={13} />Valid</span>
                    : <span className="text-sm font-bold text-red-400">Inactiv</span>}
                </div>
                {a.ssl?.issuer && (
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm text-white/50">Emitent SSL</span>
                    <span className="text-xs text-white/60 max-w-48 text-right">{a.ssl.issuer}</span>
                  </div>
                )}
                {a.ssl?.expires && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-white/50">SSL expiră</span>
                    <span className="text-sm text-white/60">{a.ssl.expires}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── WHOIS TAB ── */}
      {tab === 'whois' && a?.whois && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Globe size={15} className="text-[#4ab8ff]" />Date domeniu</h3>
              {[
                ['Domeniu', a.whois.domain],
                ['Registrar', a.whois.registrar],
                ['Înregistrat', a.whois.registeredOn],
                ['Expiră', a.whois.expiresOn],
                ['Actualizat', a.whois.updatedOn],
                ['Status', a.whois.status],
              ].map(([k, v]) => v && (
                <div key={k} className="flex items-start justify-between py-2 border-b border-white/5 gap-3">
                  <span className="text-xs text-white/40 uppercase tracking-wide flex-shrink-0">{k}</span>
                  <span className="text-sm text-white/80 text-right">{v}</span>
                </div>
              ))}
              {a.whois.nameservers.length > 0 && (
                <div className="py-2">
                  <div className="text-xs text-white/40 uppercase tracking-wide mb-2">Nameservere</div>
                  {a.whois.nameservers.map(ns => <div key={ns} className="text-sm text-white/60 font-mono">{ns}</div>)}
                </div>
              )}
            </div>

            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Building2 size={15} className="text-[#4ab8ff]" />Date proprietar</h3>
              {[
                ['Titular', a.whois.ownerName],
                ['CUI', a.whois.ownerCui],
                ['Adresă', a.whois.ownerAddress],
                ['Email', a.whois.ownerEmail],
                ['Telefon', a.whois.ownerPhone],
              ].map(([k, v]) => v && (
                <div key={k} className="flex items-start justify-between py-2 border-b border-white/5 gap-3">
                  <span className="text-xs text-white/40 uppercase tracking-wide flex-shrink-0">{k}</span>
                  <span className="text-sm text-white/80 text-right break-all">{v}</span>
                </div>
              ))}
              {a.whois.ownerCui && !a.company && (
                <div className="pt-2 text-xs text-yellow-400 flex items-center gap-1.5">
                  <AlertTriangle size={12} />Date companie indisponibile din ANAF
                </div>
              )}
            </div>
          </div>

          {/* Domain expiry alert */}
          {a.whois.expiresOn && (() => {
            const exp = new Date(a.whois.expiresOn);
            const daysLeft = Math.ceil((exp.getTime() - Date.now()) / 86400000);
            return daysLeft < 90 ? (
              <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border text-sm ${daysLeft < 30 ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300'}`}>
                <AlertTriangle size={16} className="flex-shrink-0" />
                Domeniu expiră în <strong>{daysLeft} zile</strong> ({a.whois.expiresOn}) — oportunitate de contact!
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* ── COMPANIE TAB ── */}
      {tab === 'companie' && a?.company && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Registration data */}
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Building2 size={15} className="text-[#4ab8ff]" />Date înregistrare ONRC</h3>
              {[
                ['Denumire', a.company.name],
                ['CUI', a.company.cui],
                ['Nr. Reg. Com.', a.company.registrationNumber],
                ['Data înreg.', a.company.registrationDate],
                ['Status', a.company.status],
                ['Plătitor TVA', a.company.vatPayer ? 'DA' : 'NU'],
                ['Capital social', a.company.capital],
                ['CAEN', a.company.caen],
              ].map(([k, v]) => v && (
                <div key={k} className="flex items-start justify-between py-2 border-b border-white/5 gap-3">
                  <span className="text-xs text-white/40 uppercase tracking-wide flex-shrink-0">{k}</span>
                  <span className="text-sm text-white/80 text-right">{String(v)}</span>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white">Contact & Adresă</h3>
              <div className="space-y-3">
                {a.company.address && <div className="flex items-start gap-2.5 text-sm text-white/60"><MapPin size={14} className="text-[#4ab8ff] flex-shrink-0 mt-0.5" />{a.company.address}</div>}
                {a.company.phone && <div className="flex items-center gap-2.5 text-sm text-white/60"><Phone size={14} className="text-[#4ab8ff] flex-shrink-0" />{a.company.phone}</div>}
                {a.whois?.ownerEmail && <div className="flex items-center gap-2.5 text-sm text-white/60"><Mail size={14} className="text-[#4ab8ff] flex-shrink-0" />{a.whois.ownerEmail}</div>}
              </div>
              <div className="pt-3 border-t border-white/5">
                <div className="text-xs text-white/30 flex items-center gap-1.5"><Calendar size={11} />Sursă: {a.company.source}</div>
              </div>
            </div>
          </div>

          {/* Revenue chart */}
          {a.company.revenue.length > 0 && (
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-5">Cifra de afaceri — ultimii {a.company.revenue.length} ani</h3>
              <RevenueChart data={a.company.revenue} />
              <p className="text-xs text-white/20 mt-4">Sursa: Ministerul Finanțelor / ANAF · Date publice</p>
            </div>
          )}
        </div>
      )}

      {/* ── NOTE & CONTACT TAB ── */}
      {tab === 'note' && (
        <div className="space-y-5 max-w-2xl">
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white">Contact prospect</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { label: 'Persoană contact', key: 'contactName' as const, placeholder: 'Ionescu Alexandru' },
                { label: 'Email', key: 'contactEmail' as const, placeholder: 'contact@firma.ro' },
                { label: 'Telefon', key: 'contactPhone' as const, placeholder: '+40 7XX XXX XXX' },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">{label}</label>
                  <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
                </div>
              ))}
              <div>
                <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as ProspectStatus }))}
                  className="w-full bg-[#0a0c12] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8]">
                  {(Object.entries(STATUS_CFG) as [ProspectStatus, { ro: string }][]).map(([s, { ro }]) => <option key={s} value={s}>{ro}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">Prioritate</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as 'low'|'medium'|'high' }))}
                  className="w-full bg-[#0a0c12] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8]">
                  <option value="low">Scăzută</option><option value="medium">Medie</option><option value="high">Înaltă 🔥</option>
                </select>
              </div>
            </div>
          </div>
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
            <label className="block text-xs text-white/40 mb-2 uppercase tracking-wide font-semibold">Note interne</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={5}
              placeholder="Observații, oportunități, interacțiuni anterioare..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors resize-none" />
          </div>
        </div>
      )}
    </div>
  );
}
